import type {
  CatalystLiteratureAnswer,
  ModelExpertModifiedConfigResponse,
  ModelExpertResponse,
} from "@citrine/resources/catalyst";
import { isModifiedConfigResponse } from "@citrine/resources/catalyst";
import { isOfType } from "@citrine/resources/utils";

import type { CatalystMode } from "#src/contexts/catalyst";

export interface CatalystForm {
  question: string;
}

export interface PredictorChangeHandlers {
  applyPredictorChange?: (
    questionAndAnswerId: string,
    answer: ModelExpertModifiedConfigResponse
  ) => Promise<void>;
  rejectPredictorChange?: (questionAndAnswerId: string) => void;
}

export interface SwitchToModelExpertPromptHandlers {
  onSwitchToModelExpert: (questionAndAnswerId: string) => void;
  onDeclineSwitchToModelExpert: (questionAndAnswerId: string) => void;
}

export type QuestionAndAnswerState =
  | "applied"
  | "applying"
  | "idle"
  | "rejected";

interface QuestionAndAnswerBase<
  Type extends string,
  Mode extends CatalystMode = CatalystMode,
> {
  type: Type;
  catalystMode: Mode;
  id: string;
}

type AnsweredOrErrored<Answer> =
  | {
      /**
       * `answer` is optional because it takes 10-15 seconds to get a response -
       * making it optional allows us to preserve question/answer order.
       */
      answer?: Answer;
      error?: undefined;
    }
  | {
      answer?: undefined;
      error: string;
    };

type ExpertAnswer = CatalystLiteratureAnswer | ModelExpertResponse;

type ExpertQuestionAndAnswer<
  Mode extends CatalystMode,
  Answer extends ExpertAnswer,
> = QuestionAndAnswerBase<"question", Mode> &
  AnsweredOrErrored<Answer> & {
    question: string;
  };

export type LiteratureQuestionAndAnswer = ExpertQuestionAndAnswer<
  "literature-expert",
  CatalystLiteratureAnswer
>;

export type ModeChangeStatement = QuestionAndAnswerBase<"mode-change">;
export type ModelQuestionAndAnswer<
  Answer extends ModelExpertResponse = ModelExpertResponse,
> = ExpertQuestionAndAnswer<"model-expert", Answer> &
  (Answer extends ModelExpertModifiedConfigResponse
    ? {
        automaticallyDiscardedMessage?: string;
        state?: QuestionAndAnswerState;
      }
    : object);

export type ModifiedConfigModelQuestionAndAnswer =
  ModelQuestionAndAnswer<ModelExpertModifiedConfigResponse>;

export type SwitchToModelExpertPrompt =
  QuestionAndAnswerBase<"switch-to-model-expert-prompt"> & {
    automaticallyResolvedMessage?: string;
    state: "idle" | "yes" | "no" | "resolved";
  };

export type CatalystQuestionAndAnswer =
  | LiteratureQuestionAndAnswer
  | ModeChangeStatement
  | ModelQuestionAndAnswer
  | SwitchToModelExpertPrompt;

export const isLiteratureQuestionAndAnswer = (
  questionAndAnswer: CatalystQuestionAndAnswer
): questionAndAnswer is LiteratureQuestionAndAnswer =>
  questionAndAnswer.catalystMode === "literature-expert" &&
  questionAndAnswer.type === "question";

export const isModeChangeStatement = isOfType<
  ModeChangeStatement,
  CatalystQuestionAndAnswer
>("mode-change");

export const isModelQuestionAndAnswer = (
  questionAndAnswer: CatalystQuestionAndAnswer
): questionAndAnswer is ModelQuestionAndAnswer =>
  questionAndAnswer.catalystMode === "model-expert" &&
  questionAndAnswer.type === "question";

export const isModifiedConfigModelQuestionAndAnswer = (
  questionAndAnswer: CatalystQuestionAndAnswer
): questionAndAnswer is ModifiedConfigModelQuestionAndAnswer =>
  isModelQuestionAndAnswer(questionAndAnswer) &&
  isModifiedConfigResponse(questionAndAnswer.answer);

export const isSwitchToModelExpertPrompt = isOfType<
  SwitchToModelExpertPrompt,
  CatalystQuestionAndAnswer
>("switch-to-model-expert-prompt");
